import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { DexieDb, Db } from './services/Db';
import { StyleBuilder } from './components/article/StyleBuilder';
import { Device } from './hooks/use-element';

import { SERVICE_WORKER_CHANNEL_NAME, Style } from './constants';

/**
 * React hook that keep track of the window height
 *
 */
export function useResize() {
	useEffect(() => {
		const r: any = document.querySelector(':root');
		if (!r) {
			return;
		}

		const cb = () => {
			const height = window.innerHeight;
			document.documentElement.style.setProperty(
				'--window-height',
				`${height}px`,
			);
		};
		cb();
		window.addEventListener('resize', cb);
		window.addEventListener('orientationchange', cb);
		return () => {
			window.removeEventListener('resize', cb);
		};
	}, []);
}

/**
 * React hook that returns if the PWA was installed
 *
 * @return if the pwa is installed
 *
 */
export function useInstalled(device?: Device): boolean {
	const [isInstalled, setIsInstalled] = useState(false);
	const r = document.documentElement;
	useEffect(() => {
		if (isSafari()) {
			if (r) {
				r.style.setProperty('--safe-area-inset', '20px');
			}
		}
		if (window.matchMedia('(display-mode: standalone)').matches) {
			setIsInstalled(true);

			if (!r) {
				return;
			}

			r.style.setProperty('--safe-area-inset', '20px');
		}
	}, [device]);
	return isInstalled;
}

/**
 * React hook that returns the database
 *
 * @param args - Props to get issues
 *
 */
export function useDb(): DbResponse {
	const [db, setDb] = useState<Db | null>(null);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const database = new DexieDb();
		database
			.start()
			.then(() => {
				setDb(database);
			})
			.catch((err: Error) => {
				setError(err);
			});
	}, []);

	return {
		db,
		error,
	};
}

interface DbResponse {
	db: Db | null;
	error: Error | null;
}

/**
 * React hook that receive messages from the service worker
 *
 */
export function useWorkerMessages({ db }: WorkerMessageArgs) {
	useEffect(() => {
		if (!db) {
			return;
		}
		const channel = new BroadcastChannel(SERVICE_WORKER_CHANNEL_NAME);
		const channelHandler = (event: any) => {
			const { type, payload } = event.data;
			const { issue, styles } = payload;
			switch (type) {
				case 'DOWNLOAD_ISSUE_MESSAGE':
					console.log(
						`I DOWNLOADED THE ISSUE FROM THE BACKGROUND`,
						issue,
					);
					onDownloadIssueMessage({
						issue,
						db,
					});
					break;
				case 'DOWNLOAD_STYLES_MESSAGE':
					onDownloadStylesMessage({
						styles,
						db,
					});
					break;
				default:
					break;
			}
		};
		channel.addEventListener('message', channelHandler);
		return () => {
			channel.removeEventListener('message', channelHandler);
		};
	}, [db]);
}

interface WorkerMessageArgs {
	db?: Db | null;
}

function onDownloadIssueMessage(args: DownloadIssueMessageArgs) {
	const { issue, db } = args;
	db.saveIssue(clone(issue))
		.then(() => {
			console.log(`Success caching the issue: ${issue.id}`);
		})
		.catch(console.error);
}

interface DownloadIssueMessageArgs {
	issue: any;
	db: Db;
}

function onDownloadStylesMessage(args: DownloadStylesMessageArgs) {
	const { db, styles } = args;
	const buildedStyles = buildStyles(styles);
	console.log(`I WILL STORE THIS STYLES IN THE DATABASE`, buildedStyles);
	db.saveStyles(buildedStyles)
		.then(() => {
			console.log(`Success caching ${styles.length} styles`);
		})
		.catch(console.error);
}

interface DownloadStylesMessageArgs {
	styles: any[];
	db: Db;
}

/**
 * React hook that return what was the previous location
 *
 * @return last location or base url
 *
 */
export function useBackUrl(overWriteUrl = '/'): string {
	const [url, setUrl] = useState<string>(overWriteUrl);
	const location = useLocation();
	useEffect(() => {
		const { state } = location;
		if (!state) {
			return;
		}
		const { from } = state;
		if (!from) {
			return;
		}
		setUrl(from);
	}, [location]);
	return url;
}

/**
 * React hook that load head data from article
 *
 */
export function useArticleHead(args: TitleArgs) {
	const { article, lang } = args;
	useEffect(() => {
		if (!article) {
			return;
		}
		if (!lang) {
			return;
		}

		const { name, description } = article;

		if (name[lang]) {
			document.title = name[lang];
		}

		if (description) {
			const metaHeader = document.querySelector(
				'meta[name="description"]',
			);
			if (metaHeader) {
				metaHeader.setAttribute('content', description);
			}
		}
	}, [article]);
}

interface TitleArgs {
	article: any | null | undefined;
	lang: string;
}

export function useInterval(callback: () => void, delay: number) {
	// Creating a ref
	const savedCallback: any = useRef();

	// To remember the latest callback .
	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// combining the setInterval and
	//clearInterval methods based on delay.
	useEffect(() => {
		savedCallback.current();
		function func() {
			savedCallback.current();
		}
		if (delay !== null) {
			const id = setInterval(func, delay);
			return () => clearInterval(id);
		}
	}, [delay]);
}

function buildStyles(styles: Array<Style>) {
	styles = styles.map(clone).map(StyleBuilder.mapRenameProperties);
	styles = StyleBuilder.resolveInheritance(styles);

	return styles;
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
